import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Bot, CalendarDays, FilePenLine, Loader2, Paperclip, Plus, Search, Send, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { sendAivoryOneMessage, listSuggestions, fetchAiCredits } from "@/lib/aivory-one-api";
import { renderMarkdownLiteHtml } from "@/lib/markdown-lite";
import { useEventMeta } from "@/lib/event-meta";
import { uploadMediaFile, resolveMediaUrl } from "@/lib/media-api";
import OrchestratorLiveStage from "@/components/OrchestratorLiveStage";
import WorkflowStepTimeline from "@/components/WorkflowStepTimeline";
import ThinkingProcess, { inferBusySteps } from "@/components/ThinkingProcess";
import SetupProgressPanel from "@/components/SetupProgressPanel";
import NextActionsPanel from "@/components/NextActionsPanel";

const MODULE_CHIPS = [
  { label: "Plan & Agenda", icon: CalendarDays },
  { label: "Copy & Speakers", icon: FilePenLine },
  { label: "Insights", icon: Search },
];

const STARTERS = [
  "I'm organizing a 500-person cybersecurity summit in Singapore, 12–14 Nov 2026, in-person. Create everything I need.",
  "Show me ticket sales, registrations, and revenue for this event",
  "Write speaker bios and add keynote speakers for this event",
  "Paste my brochure agenda and map it into event details + schedule",
  "Draft a save-the-date email and publish the landing page",
  "Draft a venue and AV RFP from this event brief for supplier quotes",
];

const WELCOME =
  "I'm **AivoryONE (Beta)** — a governed event-execution agent. I **Understand → Analyse → Recommend → Execute → Audit** inside your EMS: **registration**, **website**, **EDM drafts**, **agenda**, **speakers**, and **insights**. Tell me what to run — I'll confirm before publishing.";

function renderMarkdownLite(text) {
  const html = renderMarkdownLiteHtml(text);
  if (!html) return null;
  return <div className="space-y-0" dangerouslySetInnerHTML={{ __html: html }} />;
}

function ArtifactLinks({ artifacts = [] }) {
  if (!artifacts.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {artifacts.map((artifact, idx) => (
        <div key={`${artifact.type}-${idx}`} className="flex flex-wrap gap-2">
          {artifact.type === "campaign_draft" ? (
            <span className="inline-flex items-center gap-1 rounded-lg border border-[#A78BFA]/40 bg-[#A78BFA]/10 px-2.5 py-1 text-[11px] text-[#C4B5FD]">
              Campaign draft ready
            </span>
          ) : null}
          {artifact.publicUrl ? (
            <a
              href={artifact.publicUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-lg border border-[var(--primary)] px-2.5 py-1 text-[11px] text-[var(--primary)] hover:bg-[color-mix(in_srgb,var(--primary)_10%,transparent)]"
            >
              View live page
            </a>
          ) : null}
          {artifact.adminUrl ? (
            <a
              href={artifact.adminUrl}
              className="inline-flex items-center gap-1 rounded-lg border border-[var(--border-default)] px-2.5 py-1 text-[11px] text-[var(--text-secondary)] hover:bg-[var(--bg-elev)]"
            >
              {artifact.type === "campaign_draft" ? "Open communications" : "Open editor"}
            </a>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export default function AivoryOnePanel({
  compact = false,
  variant = "embedded",
  aiEnabled = true,
  eventId: eventIdProp = null,
  onClose,
  onNewChat,
  onStageChange,
  onWorkspaceUpdate,
}) {
  const isDock = variant === "dock";
  const { id: routeEventId } = useParams();
  const eventId = eventIdProp || routeEventId || null;
  const [selectedEventId, setSelectedEventId] = useState(null);
  const activeEventId = selectedEventId || eventId;
  const eventMeta = useEventMeta(activeEventId);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [needsInput, setNeedsInput] = useState(null);
  const [liveArtifact, setLiveArtifact] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [workflowRunId, setWorkflowRunId] = useState(null);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [setupProgress, setSetupProgress] = useState(null);
  const [busySteps, setBusySteps] = useState(null);
  const [nextActions, setNextActions] = useState([]);
  const [pendingRetryMessage, setPendingRetryMessage] = useState("");
  const [fieldDraft, setFieldDraft] = useState({});
  const [credits, setCredits] = useState(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("aivoryone.conversationId");
      const savedWf = localStorage.getItem("aivoryone.workflowRunId");
      if (saved) setConversationId(saved);
      if (savedWf) setWorkflowRunId(savedWf);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      if (conversationId) localStorage.setItem("aivoryone.conversationId", conversationId);
      if (workflowRunId) localStorage.setItem("aivoryone.workflowRunId", workflowRunId);
    } catch {
      /* ignore */
    }
  }, [conversationId, workflowRunId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchAiCredits();
        if (!cancelled && data) setCredits(data);
      } catch {
        /* optional */
      }
    })();
    return () => { cancelled = true; };
  }, [messages.length, busy]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listSuggestions(activeEventId);
        if (cancelled || !rows.length) return;
        const mapped = rows
          .filter((r) => !r.dismissed)
          .slice(0, 6)
          .map((r) => ({
            id: r.id,
            title: r.title,
            body: r.body,
            prompt: r.payload?.prompt || r.title,
            href: r.payload?.href,
          }));
        if (mapped.length) setNextActions((prev) => (prev.length ? prev : mapped));
      } catch {
        /* suggestions are optional */
      }
    })();
    return () => { cancelled = true; };
  }, [activeEventId]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy, liveArtifact, nextActions]);

  useEffect(() => {
    onStageChange?.(Boolean(liveArtifact?.publicUrl || liveArtifact?.previewUrl));
  }, [liveArtifact, onStageChange]);

  useEffect(() => {
    if (needsInput?.type === "fields") {
      setFieldDraft(needsInput.known || {});
    }
  }, [needsInput]);

  const pickArtifactForStage = (artifacts = []) => {
    const rows = Array.isArray(artifacts) ? artifacts : [];
    return (
      rows.find((a) => a.type === "live_page" && a.publicUrl) ||
      rows.find((a) => a.type === "page_draft" && (a.previewUrl || a.adminUrl)) ||
      rows.find((a) => a.publicUrl || a.previewUrl) ||
      rows.find((a) => a.adminUrl) ||
      null
    );
  };

  const resetChat = () => {
    setMessages([{ role: "assistant", content: WELCOME }]);
    setInput("");
    setPendingAttachments([]);
    setNeedsInput(null);
    setLiveArtifact(null);
    setConversationId(null);
    setWorkflowRunId(null);
    setWorkflowSteps([]);
    setThinkingSteps([]);
    setSetupProgress(null);
    setBusySteps(null);
    setNextActions([]);
    setSelectedEventId(null);
    setPendingRetryMessage("");
    setFieldDraft({});
    onNewChat?.();
  };

  const submit = useCallback(
    async (text, extraAttachments = [], eventIdOverride = null, selectionLabel = null) => {
      const trimmed = String(text || "").trim();
      const attachments = [...pendingAttachments, ...extraAttachments];
      if ((!trimmed && !attachments.length) || busy) return;
      if (!aiEnabled) {
        toast.error("AivoryONE (Beta) requires Studio plan", { description: "Upgrade under Billing to unlock." });
        return;
      }
      const apiEventId = eventIdOverride || selectedEventId || eventId;
      const displayLine = selectionLabel || trimmed || (attachments.length ? "Use the attached file" : "");
      setMessages((prev) => [...prev, { role: "user", content: displayLine, attachments: selectionLabel ? [] : attachments }]);
      setInput("");
      setPendingAttachments([]);
      setNeedsInput(null);
      setThinkingSteps([]);
      setSetupProgress(null);
      // Only continue a workflow on explicit continue / approval — never hijack Q&A.
      const lower = (trimmed || selectionLabel || "").toLowerCase();
      const continueWorkflow =
        Boolean(workflowRunId) &&
        (
          /^(yes|y|ok|okay|continue|proceed|go ahead|confirm|approve|do it)\b/.test(lower) ||
          lower.includes("create everything") ||
          lower.includes("set up everything") ||
          lower.includes("continue the workflow") ||
          lower.includes("continue workflow") ||
          lower.includes("next step")
        );
      const activeWorkflowId = continueWorkflow ? workflowRunId : null;
      if (!continueWorkflow) {
        if (workflowRunId) {
          setWorkflowRunId(null);
          try {
            localStorage.removeItem("aivoryone.workflowRunId");
          } catch {
            /* ignore */
          }
        }
        setWorkflowSteps([]);
      }
      setBusySteps(inferBusySteps(trimmed || selectionLabel));
      setBusy(true);
      try {
        const data = await sendAivoryOneMessage({
          message: trimmed || displayLine,
          eventId: apiEventId,
          conversationId,
          workflowRunId: activeWorkflowId,
          history: messages.map((m) => ({ role: m.role, content: m.content })),
          attachments: selectionLabel ? [] : attachments,
        });
        if (data.conversationId) setConversationId(data.conversationId);
        if (data.workflowComplete) {
          setWorkflowRunId(null);
          try {
            localStorage.removeItem("aivoryone.workflowRunId");
          } catch {
            /* ignore */
          }
        } else if (data.workflowRunId) {
          setWorkflowRunId(data.workflowRunId);
        }
        if (data.eventId) setSelectedEventId(data.eventId);
        if (Array.isArray(data.workflowSteps)) {
          setWorkflowSteps(data.workflowComplete ? [] : data.workflowSteps);
        }
        if (data.thinkingSteps?.length) setThinkingSteps(data.thinkingSteps);
        if (data.setupProgress) setSetupProgress(data.setupProgress);
        if (data.nextActions?.length) setNextActions(data.nextActions);

        const stageArtifact = pickArtifactForStage(data.artifacts || []);
        // In full workspace, preview lives in the right rail — keep dock/inline stage for dock only.
        if (stageArtifact && variant !== "embedded") setLiveArtifact(stageArtifact);
        if (data.needsInput) {
          setNeedsInput(data.needsInput);
          if (data.needsInput.type === "event_choice") {
            setPendingRetryMessage(data.needsInput.pendingMessage || trimmed || displayLine);
          }
        }

        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.reply || "Done.",
            actions: data.actions,
            artifacts: data.artifacts,
            mockMode: data.mockMode,
            workflowSteps: data.workflowSteps,
            thinkingSteps: data.thinkingSteps,
            setupProgress: data.setupProgress,
            confidence: data.confidence,
          },
        ]);

        onWorkspaceUpdate?.({
          eventId: data.eventId || apiEventId || null,
          artifacts: data.artifacts || [],
          actions: data.actions || [],
          stageArtifact,
        });

        if (data.workflowComplete) {
          toast.success("Workflow complete", { description: "Review artifacts in your workspace." });
        } else if (data.artifacts?.some((a) => a.type === "live_page")) {
          toast.success("Published live", { description: "Your public page is ready to share." });
        } else if (data.actions?.some((a) => a.name === "save_page_builder" && a.ok)) {
          toast.success("Landing page updated");
          const eid = data.eventId || eventId || selectedEventId;
          if (eid) {
            window.dispatchEvent(
              new CustomEvent("aivory-state-updated", { detail: { key: `page-builder.${eid}` } }),
            );
          }
        } else if (data.actions?.some((a) => ["configure_registration_form", "publish_registration_form"].includes(a.name) && a.ok)) {
          toast.success("Registration form updated");
          const eid = data.eventId || eventId || selectedEventId;
          if (eid) {
            window.dispatchEvent(
              new CustomEvent("aivory-state-updated", { detail: { key: `form-builder.draft.${eid}` } }),
            );
          }
        }
      } catch (err) {
        toast.error("AivoryONE (Beta) failed", { description: err.message });
        setMessages((prev) => [...prev, { role: "assistant", content: `Sorry — ${err.message}` }]);
      } finally {
        setBusy(false);
        setBusySteps(null);
      }
    },
    [aiEnabled, busy, conversationId, eventId, messages, onWorkspaceUpdate, pendingAttachments, selectedEventId, variant, workflowRunId],
  );

  const pickEvent = (opt) => {
    const retry = pendingRetryMessage || needsInput?.pendingMessage;
    if (!retry || !opt?.id) return;
    setPendingRetryMessage("");
    submit(retry, [], opt.id, opt.title);
  };

  const submitFieldForm = () => {
    const fields = needsInput?.fields || [];
    const lines = [];
    const known = needsInput?.known || {};
    Object.entries(known).forEach(([k, v]) => {
      if (v != null && String(v).trim()) lines.push(`${k}: ${v}`);
    });
    fields.forEach((f) => {
      const val = String(fieldDraft[f.id] || "").trim();
      if (val) lines.push(`${f.id}: ${val}`);
    });
    if (!lines.length) return;
    submit(lines.join("\n"));
  };

  const onPickFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of list.slice(0, 3)) {
        const asset = await uploadMediaFile(file);
        const url = resolveMediaUrl(asset?.url || asset?.path || "");
        uploaded.push({
          url,
          name: asset?.filename || file.name,
          kind: asset?.mime || file.type,
          assetId: asset?.id,
        });
      }
      setPendingAttachments((prev) => [...prev, ...uploaded]);
      if (needsInput?.type === "attachment") {
        await submit(input || "Use this attachment", uploaded);
      }
    } catch (err) {
      toast.error("Upload failed", { description: err.message });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const starterPrompts = nextActions.length
    ? nextActions.slice(0, 4).map((a) => a.prompt || a.title)
    : STARTERS;

  const showStarters = !messages.some((m) => m.role === "user");

  return (
    <div
      className={`flex flex-col min-h-0 bg-[var(--bg-surface)] ${
        isDock ? "h-full" : `border border-[var(--border-default)] ${compact ? "h-[min(70vh,640px)] rounded-lg" : "min-h-[520px] rounded-xl"}`
      }`}
      data-testid="aivory-one-panel"
    >
      {isDock ? (
        <div className="shrink-0 px-4 py-4 border-b border-[var(--border-default)] bg-gradient-to-r from-[color-mix(in_srgb,var(--primary)_18%,var(--bg-surface))] to-[var(--bg-surface)]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl grid place-items-center bg-[var(--primary)] text-[var(--on-primary,#fff)] shadow-sm">
                  <Sparkles size={18} />
                </div>
                <div>
                  <div className="text-base font-semibold text-[var(--text-primary)]">AivoryONE (Beta)</div>
                  <div className="text-xs text-[var(--text-secondary)]">Registration, website, EDM, agenda — I show my thinking.</div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button type="button" className="h-8 w-8 grid place-items-center rounded-lg text-[var(--text-tertiary)] hover:bg-[var(--bg-elev)]" aria-label="New chat" onClick={resetChat}>
                <Plus size={16} />
              </button>
              <button type="button" className="h-8 w-8 grid place-items-center rounded-lg text-[var(--text-tertiary)] hover:bg-[var(--bg-elev)]" aria-label="Close" onClick={onClose}>
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--border-default)]">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-full grid place-items-center bg-[var(--bg-elev)] text-[var(--primary)]">
              <Bot size={16} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">AivoryONE (Beta)</div>
              <div className="text-[10px] text-[var(--text-tertiary)] font-mono truncate">
                {activeEventId ? eventMeta?.title || activeEventId : "workspace · pick an event when asked"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {credits && credits.monthlyAllowance > 0 ? (
              <div className="text-[10px] font-mono text-[var(--text-tertiary)]" title="AI credits this month">
                {credits.remaining ?? 0}/{credits.monthlyAllowance} credits
              </div>
            ) : null}
            {eventId ? (
              <Link to={`/admin/events/${eventId}/landing`} className="text-xs text-[var(--primary)] hover:underline shrink-0">
                Page Builder
              </Link>
            ) : null}
          </div>
        </div>
      )}

      {isDock && credits && credits.monthlyAllowance > 0 ? (
        <div className="shrink-0 px-4 py-1.5 border-b border-[var(--border-default)] text-[10px] font-mono text-[var(--text-tertiary)]">
          AI credits: {credits.remaining ?? 0} remaining of {credits.monthlyAllowance} this month
        </div>
      ) : null}

      {variant !== "embedded" && liveArtifact ? (
        <OrchestratorLiveStage artifact={liveArtifact} onClose={() => setLiveArtifact(null)} />
      ) : null}

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-4 space-y-4">
          {messages.map((m, idx) => (
            <div key={idx} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[92%] rounded-2xl px-3.5 py-2.5 ${m.role === "user" ? "bg-[var(--primary)] text-[var(--on-primary,#000)]" : "bg-[var(--bg-elev)] text-[var(--text-primary)]"}`}>
                {m.role === "user" ? (
                  <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                ) : (
                  <>
                    {m.thinkingSteps?.length ? (
                      <ThinkingProcess
                        steps={m.thinkingSteps}
                        confidence={m.confidence}
                        defaultOpen={idx === messages.length - 1}
                        streamReveal={idx === messages.length - 1}
                      />
                    ) : null}
                    {m.workflowSteps?.length ? <WorkflowStepTimeline steps={m.workflowSteps} /> : null}
                    {m.setupProgress ? <SetupProgressPanel progress={m.setupProgress} /> : null}
                    {m.confidence?.label === "low" ? (
                      <p className="text-[10px] font-mono text-[var(--text-tertiary)] mb-2">
                        Low confidence — please verify manually.
                      </p>
                    ) : null}
                    {renderMarkdownLite(m.content)}
                    <ArtifactLinks artifacts={m.artifacts} />
                  </>
                )}
                {m.mockMode ? <p className="text-[10px] mt-2 opacity-70 font-mono">demo mode</p> : null}
              </div>
            </div>
          ))}
          {busy ? (
            <div className="flex justify-start">
              <div className="max-w-[92%] rounded-2xl px-3.5 py-2.5 bg-[var(--bg-elev)] text-[var(--text-primary)] w-full">
                <ThinkingProcess busy busySteps={busySteps} defaultOpen />
                {workflowSteps.length ? <WorkflowStepTimeline steps={workflowSteps} /> : null}
              </div>
            </div>
          ) : null}
          {!busy && nextActions.length ? (
            <NextActionsPanel actions={nextActions} onSelect={(prompt) => submit(prompt)} />
          ) : null}
          {needsInput?.type === "confirm" ? (
            <div className="rounded-xl border border-[var(--primary)] bg-[color-mix(in_srgb,var(--primary)_8%,var(--bg-surface))] p-3 space-y-3" data-testid="aivory-one-confirm">
              <p className="text-sm whitespace-pre-wrap">{needsInput.prompt || "Confirm these actions?"}</p>
              {(needsInput.summary || []).length ? (
                <ul className="text-[11px] font-mono text-[var(--text-tertiary)] space-y-1">
                  {(needsInput.summary || []).map((name) => (
                    <li key={name}>• {name}</li>
                  ))}
                </ul>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-primary text-xs"
                  disabled={busy}
                  onClick={async () => {
                    const tools = needsInput.tools || [];
                    setNeedsInput(null);
                    setBusy(true);
                    setBusySteps(inferBusySteps("confirm"));
                    try {
                      const data = await sendAivoryOneMessage({
                        message: "yes",
                        eventId: eventId || selectedEventId,
                        conversationId,
                        confirmedTools: tools,
                        history: messages.map((m) => ({ role: m.role, content: m.content })),
                      });
                      if (data.conversationId) setConversationId(data.conversationId);
                      setMessages((prev) => [
                        ...prev,
                        { role: "user", content: "Confirm" },
                        {
                          role: "assistant",
                          content: data.reply || "Done.",
                          actions: data.actions,
                          artifacts: data.artifacts,
                          thinkingSteps: data.thinkingSteps,
                          confidence: data.confidence,
                          mockMode: data.mockMode,
                        },
                      ]);
                      onWorkspaceUpdate?.({
                        eventId: data.eventId || eventId || selectedEventId || null,
                        artifacts: data.artifacts || [],
                        actions: data.actions || [],
                      });
                    } catch (err) {
                      toast.error(err?.message || "Could not confirm actions");
                    } finally {
                      setBusy(false);
                      setBusySteps(null);
                    }
                  }}
                >
                  Confirm & run
                </button>
                <button type="button" className="btn-secondary text-xs" disabled={busy} onClick={() => setNeedsInput(null)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
          {needsInput?.type === "attachment" ? (
            <div className="rounded-xl border border-dashed border-[var(--primary)] p-3">
              <p className="text-sm">{needsInput.prompt || "Upload a file to continue"}</p>
              <button type="button" className="btn-secondary text-xs mt-3" onClick={() => fileRef.current?.click()} disabled={busy}>
                <Paperclip size={12} /> Choose file
              </button>
            </div>
          ) : null}
          {needsInput?.type === "event_choice" ? (
            <div className="rounded-xl border border-[var(--primary)] bg-[color-mix(in_srgb,var(--primary)_8%,var(--bg-surface))] p-3 space-y-2" data-testid="aivory-one-event-choice">
              <p className="text-sm font-medium text-[var(--text-primary)]">{needsInput.prompt || "Which event?"}</p>
              <div className="space-y-2">
                {(needsInput.options || []).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className="w-full text-left rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] px-3 py-2.5 hover:border-[var(--primary)]"
                    onClick={() => pickEvent(opt)}
                    disabled={busy}
                  >
                    <div className="text-sm font-medium text-[var(--text-primary)]">{opt.title}</div>
                    <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
                      {[opt.venue, opt.startDate, opt.status].filter(Boolean).join(" · ")}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {needsInput?.type === "fields" ? (
            <div className="rounded-xl border border-[var(--primary)] bg-[color-mix(in_srgb,var(--primary)_8%,var(--bg-surface))] p-3 space-y-3" data-testid="aivory-one-field-intake">
              <p className="text-sm font-medium text-[var(--text-primary)]">{needsInput.prompt || "A few more details"}</p>
              {Object.keys(needsInput.known || {}).length ? (
                <p className="text-[11px] text-[var(--text-tertiary)] font-mono">
                  Already noted: {Object.entries(needsInput.known).map(([k, v]) => `${k}=${v}`).join(" · ")}
                </p>
              ) : null}
              {(needsInput.fields || []).map((field) => (
                <label key={field.id} className="block">
                  <span className="text-xs text-[var(--text-secondary)]">{field.label}</span>
                  <input
                    className="input-dark w-full mt-1 text-sm"
                    placeholder={field.placeholder || ""}
                    value={fieldDraft[field.id] || ""}
                    onChange={(e) => setFieldDraft((prev) => ({ ...prev, [field.id]: e.target.value }))}
                    disabled={busy}
                  />
                </label>
              ))}
              <button
                type="button"
                className="btn-primary text-xs"
                disabled={busy || !(needsInput.fields || []).every((f) => String(fieldDraft[f.id] || "").trim())}
                onClick={submitFieldForm}
              >
                Continue with these details
              </button>
            </div>
          ) : null}
          {showStarters ? (
            <div className="space-y-3">
              <div className="flex flex-col gap-2">
                {starterPrompts.map((s) => (
                  <button key={s} type="button" className="text-left text-sm rounded-xl border border-[var(--border-default)] bg-[var(--bg-elev)] px-3.5 py-2.5 hover:border-[var(--primary)]" onClick={() => submit(s)} disabled={busy}>
                    {s}
                  </button>
                ))}
              </div>
              {isDock ? (
                <div className="flex items-center gap-4 text-[11px] text-[var(--text-tertiary)]">
                  {MODULE_CHIPS.map(({ label, icon: Icon }) => (
                    <span key={label} className="inline-flex items-center gap-1.5">
                      <Icon size={12} className="text-[var(--primary)]" />
                      {label}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          <div ref={scrollRef} />
        </div>
      </div>

      <form className="shrink-0 p-4 border-t border-[var(--border-default)]" onSubmit={(e) => { e.preventDefault(); submit(input); }}>
        <input ref={fileRef} type="file" className="hidden" accept={needsInput?.accept || "image/*,.pdf"} multiple onChange={(e) => onPickFiles(e.target.files)} />
        <div className="flex gap-2 items-end">
          <button type="button" className="shrink-0 h-11 w-11 grid place-items-center rounded-xl border border-[var(--border-default)]" onClick={() => fileRef.current?.click()} disabled={busy}>
            <Paperclip size={16} />
          </button>
          <textarea
            rows={1}
            className="input-dark flex-1 text-sm resize-none rounded-xl min-h-[44px]"
            placeholder="Tell AivoryONE (Beta) what you're organizing…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(input); } }}
            disabled={busy}
            data-testid="aivory-one-input"
          />
          <button type="submit" className="shrink-0 h-11 w-11 grid place-items-center rounded-xl bg-[var(--primary)] text-[var(--on-primary,#fff)] disabled:opacity-50" disabled={busy || (!input.trim() && !pendingAttachments.length)}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </form>
    </div>
  );
}
